import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyDollar, TrendUp, TrendDown, Truck, GasPump, Trophy, MapPin, Receipt, ChartLineUp, Calendar, Crown, FilePdf } from "@phosphor-icons/react";

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtNum = (n, d = 0) => (Number(n) || 0).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });

function DeltaBadge({ value, invert = false }) {
  const n = Number(value) || 0;
  if (n === 0) return <span className="text-xs text-slate-500 font-semibold">—</span>;
  // invert=true → red when positive (ex: expenses growing is bad)
  const good = invert ? n < 0 : n > 0;
  const Icon = n > 0 ? TrendUp : TrendDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold ${good ? "text-emerald-300" : "text-rose-300"}`}>
      <Icon size={14} weight="bold" />
      {n > 0 ? "+" : ""}{fmtNum(n, 1)}%
    </span>
  );
}

export default function OwnerDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [compareMode, setCompareMode] = useState("previous");  // 'previous' | 'yoy'
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const load = () => {
    setLoading(true);
    let url = `/dashboard/owner?period=${period}&compare=${compareMode}`;
    if (period === "custom" && customStart && customEnd) url += `&start=${customStart}&end=${customEnd}`;
    api.get(url)
      .then((r) => setData(r.data))
      .catch((e) => setData({ error: e?.response?.data?.detail || "Erro ao carregar dados" }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, compareMode]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("live-event", h);
    return () => window.removeEventListener("live-event", h);
    // eslint-disable-next-line
  }, [period, customStart, customEnd]);

  const applyCustom = () => {
    if (customStart && customEnd) load();
  };

  const downloadPdf = async (kind) => {
    let url = `/reports/pdf/${kind}?period=${period}`;
    if (period === "custom" && customStart && customEnd) url += `&start=${customStart}&end=${customEnd}`;
    try {
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${kind}_${(data?.period?.start) || ""}_${(data?.period?.end) || ""}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (e) {
      alert("Erro ao gerar PDF: " + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading && !data) {
    return <p className="text-muted-foreground" data-testid="owner-loading">Carregando painel…</p>;
  }
  if (data?.error) {
    return (
      <Card className="border-2 border-amber-300 bg-amber-50">
        <CardContent className="p-6 text-amber-900 font-semibold" data-testid="owner-error">{data.error}</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const { current, previous, deltas, consumption_by_vehicle, top_clients, top_routes, profit_by_vehicle, role, compare_mode } = data;
  const profitMargin = current.revenue > 0 ? (current.profit / current.revenue) * 100 : 0;
  const profitColor = current.profit >= 0 ? "from-emerald-400 to-emerald-600" : "from-rose-400 to-rose-600";
  const prevLabel = compare_mode === "yoy" ? "Ano passado" : (period === "year" ? "Ano anterior" : period === "custom" ? "Período anterior" : "Mês anterior");

  return (
    <div className="space-y-6" data-testid="owner-dashboard">
      {/* HEADER — DONO PANEL */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 p-6 md:p-8 shadow-2xl border border-slate-700">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #60a5fa 0%, transparent 40%), radial-gradient(circle at 80% 70%, #10b981 0%, transparent 40%)" }} />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-widest">
              <Crown size={16} weight="fill" /> Painel do Dono
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white mt-1 tracking-tight" style={{ fontFamily: "Manrope" }}>
              Como anda meu caminhão?
            </h1>
            <p className="text-slate-300 text-sm mt-2 max-w-xl">
              Lucro, custo por km, consumo e os clientes/rotas que mais trazem dinheiro.
            </p>
          </div>
          {/* Filtro de período */}
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { k: "month", label: "Este mês" },
              { k: "year", label: "Este ano" },
              { k: "custom", label: "Período" },
            ].map((p) => (
              <Button
                key={p.k}
                size="sm"
                data-testid={`owner-period-${p.k}`}
                onClick={() => setPeriod(p.k)}
                className={`${period === p.k ? "bg-white text-slate-900 hover:bg-slate-100" : "bg-white/10 text-white hover:bg-white/20"} border border-white/20`}
              >
                <Calendar size={14} weight="bold" className="mr-1" /> {p.label}
              </Button>
            ))}
          </div>
        </div>
        {period === "custom" && (
          <div className="relative mt-4 flex items-center gap-2 flex-wrap">
            <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="owner-custom-start" className="w-40 bg-white/90" />
            <span className="text-white">até</span>
            <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="owner-custom-end" className="w-40 bg-white/90" />
            <Button onClick={applyCustom} data-testid="owner-custom-apply" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">Aplicar</Button>
          </div>
        )}
        <div className="relative mt-4 text-xs text-slate-400">
          Período: <strong className="text-slate-200">{data.period.start}</strong> até <strong className="text-slate-200">{data.period.end}</strong> · Comparando com {data.previous_period.start} → {data.previous_period.end}
          <div className="mt-2 flex items-center gap-2">
            <span className="text-slate-300">Comparar com:</span>
            {[
              { k: "previous", l: period === "year" ? "Ano anterior" : period === "custom" ? "Período anterior" : "Mês anterior" },
              { k: "yoy", l: "Mesmo período do ano passado" },
            ].map((c) => (
              <button
                key={c.k}
                data-testid={`owner-compare-${c.k}`}
                onClick={() => setCompareMode(c.k)}
                className={`px-2 py-1 rounded text-xs font-bold border ${compareMode === c.k ? "bg-amber-400 text-slate-900 border-amber-400" : "bg-white/10 text-white border-white/20 hover:bg-white/20"}`}
              >
                {c.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPIs — 3 CARDS GRANDES */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Faturamento */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 p-6 shadow-xl text-white" data-testid="owner-kpi-revenue">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-80 flex items-center gap-1"><Receipt size={14} weight="bold" /> Faturamento</div>
              <div className="text-3xl md:text-4xl font-black mt-2" style={{ fontFamily: "Manrope" }}>{fmt(current.revenue)}</div>
            </div>
            <DeltaBadge value={deltas.revenue} />
          </div>
          <div className="mt-3 text-xs opacity-80">{prevLabel}: {fmt(previous.revenue)}</div>
        </div>

        {/* Despesas */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-rose-500 to-rose-700 p-6 shadow-xl text-white" data-testid="owner-kpi-expenses">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-80 flex items-center gap-1"><TrendDown size={14} weight="bold" /> Despesas</div>
              <div className="text-3xl md:text-4xl font-black mt-2" style={{ fontFamily: "Manrope" }}>{fmt(current.expenses)}</div>
            </div>
            <DeltaBadge value={deltas.expenses} invert />
          </div>
          <div className="mt-3 text-xs opacity-80">{prevLabel}: {fmt(previous.expenses)}</div>
        </div>

        {/* Lucro */}
        <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${profitColor} p-6 shadow-xl text-white`} data-testid="owner-kpi-profit">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-80 flex items-center gap-1"><ChartLineUp size={14} weight="bold" /> Lucro Líquido</div>
              <div className="text-3xl md:text-4xl font-black mt-2" style={{ fontFamily: "Manrope" }}>{fmt(current.profit)}</div>
            </div>
            <DeltaBadge value={deltas.profit} />
          </div>
          <div className="mt-3 text-xs opacity-90 flex items-center justify-between">
            <span>Margem: <strong>{fmtNum(profitMargin, 1)}%</strong></span>
            <span className="opacity-70">{prevLabel}: {fmt(previous.profit)}</span>
          </div>
        </div>
      </div>

      {/* KPIs secundários */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { l: "KM Rodados", v: `${fmtNum(current.km, 0)} km`, i: Truck, c: "text-slate-700" },
          { l: "Custo por KM", v: fmt(current.cost_per_km), i: CurrencyDollar, c: "text-rose-700" },
          { l: "Faturamento / KM", v: fmt(current.revenue_per_km), i: TrendUp, c: "text-emerald-700" },
          { l: "Viagens", v: fmtNum(current.trip_count, 0), i: Receipt, c: "text-blue-700" },
        ].map((k) => (
          <Card key={k.l} className="border-2 border-slate-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><k.i size={12} weight="bold" /> {k.l}</div>
              <div className={`text-2xl font-black mt-1 ${k.c}`} style={{ fontFamily: "Manrope" }}>{k.v}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* PDF Export Buttons */}
      <Card className="border-2 border-blue-200 bg-blue-50/40">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="font-bold text-slate-900 flex items-center gap-1"><FilePdf size={20} weight="duotone" className="text-blue-700" /> Exportar relatórios em PDF</div>
              <div className="text-xs text-slate-600">Perfeito pra enviar pra contabilidade do período selecionado.</div>
            </div>
            <Button onClick={() => downloadPdf("trips")} data-testid="pdf-trips" className="bg-blue-600 hover:bg-blue-700 text-white">
              <FilePdf size={14} className="mr-1" /> Fretes
            </Button>
            <Button onClick={() => downloadPdf("expenses")} data-testid="pdf-expenses" className="bg-rose-600 hover:bg-rose-700 text-white">
              <FilePdf size={14} className="mr-1" /> Despesas
            </Button>
            <Button onClick={() => downloadPdf("fueling")} data-testid="pdf-fueling" className="bg-amber-600 hover:bg-amber-700 text-white">
              <FilePdf size={14} className="mr-1" /> Abastecimentos
            </Button>
          </div>
        </CardContent>
      </Card>


      {/* Breakdown de despesas */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Receipt size={20} weight="duotone" className="text-rose-600" /> Para onde foi o dinheiro
          </CardTitle>
        </CardHeader>
        <CardContent>
          {current.expenses === 0 ? (
            <p className="text-sm text-slate-500">Sem despesas no período.</p>
          ) : (
            <div className="space-y-2">
              {[
                { k: "Comissão motorista", v: current.breakdown.commission, c: "bg-amber-500" },
                { k: "Diesel", v: current.breakdown.diesel, c: "bg-blue-600" },
                { k: "ARLA", v: current.breakdown.arla, c: "bg-sky-400" },
                { k: "Despesas (pedágios, estac., etc)", v: current.breakdown.other_expenses, c: "bg-rose-500" },
                { k: "Manutenção", v: current.breakdown.maintenance, c: "bg-violet-500" },
              ].filter((x) => x.v > 0).map((row) => {
                const pct = current.expenses > 0 ? (row.v / current.expenses) * 100 : 0;
                return (
                  <div key={row.k} data-testid={`owner-breakdown-${row.k}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-700">{row.k}</span>
                      <span className="font-bold text-slate-900">{fmt(row.v)} <span className="text-xs text-slate-500">({fmtNum(pct, 1)}%)</span></span>
                    </div>
                    <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${row.c} rounded-full`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top 5 Clientes */}
      {top_clients.length > 0 && (
        <Card data-testid="owner-top-clients">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy size={20} weight="duotone" className="text-amber-600" /> Top 5 Clientes do período
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {top_clients.map((c, idx) => (
              <div key={c.client_name} data-testid={`owner-client-row-${idx}`} className="flex items-center gap-3 border rounded-lg p-3 bg-white">
                <div className={`w-9 h-9 rounded-full font-black flex items-center justify-center text-white ${idx === 0 ? "bg-amber-400" : idx === 1 ? "bg-slate-400" : idx === 2 ? "bg-orange-400" : "bg-blue-600"}`}>
                  {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900 truncate">{c.client_name}</div>
                  <div className="text-xs text-slate-500">{c.trips} viagem(ns)</div>
                </div>
                <div className="text-xl font-black text-emerald-700" style={{ fontFamily: "Manrope" }}>{fmt(c.revenue)}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Top 5 Rotas */}
      {top_routes.length > 0 && (
        <Card data-testid="owner-top-routes">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin size={20} weight="duotone" className="text-blue-600" /> Top 5 Rotas mais lucrativas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {top_routes.map((r, idx) => (
              <div key={r.route} data-testid={`owner-route-row-${idx}`} className="flex items-center gap-3 border rounded-lg p-3 bg-white">
                <div className={`w-9 h-9 rounded-full font-black flex items-center justify-center text-white ${idx === 0 ? "bg-amber-400" : idx === 1 ? "bg-slate-400" : idx === 2 ? "bg-orange-400" : "bg-blue-600"}`}>
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900 truncate">{r.route}</div>
                  <div className="text-xs text-slate-500">{r.trips} viagem(ns)</div>
                </div>
                <div className="text-xl font-black text-emerald-700" style={{ fontFamily: "Manrope" }}>{fmt(r.revenue)}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Consumo médio por veículo */}
      {consumption_by_vehicle.length > 0 && (
        <Card data-testid="owner-consumption" className="border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <GasPump size={20} weight="duotone" className="text-blue-600" /> Consumo médio por caminhão (km/L)
            </CardTitle>
            <p className="text-xs text-slate-600">Baseado nos abastecimentos de diesel do período.</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {consumption_by_vehicle.map((v) => {
              const cfg = v.status === "green"
                ? { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", label: "Ótimo" }
                : v.status === "amber"
                ? { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", label: "Atenção" }
                : v.status === "red"
                ? { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", label: "Baixo" }
                : { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-600", label: "Sem dados" };
              return (
                <div key={v.vehicle_id} data-testid={`owner-cons-row-${v.vehicle_id}`} className={`flex items-center gap-3 border rounded-lg p-3 flex-wrap ${cfg.bg} ${cfg.border}`}>
                  <Truck size={22} weight="fill" className="text-blue-600" />
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-bold text-slate-900">{v.plate_truck} {v.model && <span className="text-slate-500 font-normal">— {v.model}</span>}</div>
                    <div className="text-xs text-slate-600">{v.fueling_count} abast. · {fmtNum(v.km, 0)} km · {fmtNum(v.liters, 2)} L · Gasto: {fmt(v.cost)}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-black ${cfg.text}`} style={{ fontFamily: "Manrope" }}>{fmtNum(v.avg_km_per_liter, 2)} km/L</div>
                    <div className={`text-xs font-semibold ${cfg.text}`}>{cfg.label}</div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Lucro por caminhão — SÓ EMPRESA */}
      {role === "company" && profit_by_vehicle.length > 0 && (
        <Card data-testid="owner-profit-by-vehicle" className="border-2 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Truck size={20} weight="duotone" className="text-emerald-700" /> Lucro por caminhão
            </CardTitle>
            <p className="text-xs text-slate-600">Qual caminhão da frota está dando mais retorno no período.</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {profit_by_vehicle.map((v, idx) => {
              const profitColor = v.profit >= 0 ? "text-emerald-700" : "text-rose-700";
              return (
                <div key={v.vehicle_id} data-testid={`owner-vehicle-profit-${v.vehicle_id}`} className="flex items-center gap-3 border rounded-lg p-3 flex-wrap bg-white">
                  <div className={`w-9 h-9 rounded-full font-black flex items-center justify-center text-white ${idx === 0 ? "bg-amber-400" : "bg-slate-500"}`}>
                    {idx === 0 ? "🥇" : idx + 1}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-bold text-slate-900">{v.plate_truck} {v.model && <span className="text-slate-500 font-normal">— {v.model}</span>}</div>
                    <div className="text-xs text-slate-600">{v.trips} viagem(ns) · Fat.: {fmt(v.revenue)} · Desp.: {fmt(v.expenses)}</div>
                  </div>
                  <div className={`text-xl font-black ${profitColor}`} style={{ fontFamily: "Manrope" }}>{fmt(v.profit)}</div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {current.trip_count === 0 && (
        <Card className="border-2 border-dashed border-slate-300">
          <CardContent className="p-8 text-center">
            <CurrencyDollar size={48} weight="duotone" className="text-slate-400 mx-auto" />
            <p className="mt-3 text-slate-600 font-semibold">Nenhuma viagem registrada no período selecionado.</p>
            <p className="text-xs text-slate-500 mt-1">Cadastre fretes e abastecimentos para ver o painel.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
